import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Loader2, ChevronRight, Mic, Volume2 } from 'lucide-react';
import { apiClient } from '../services/api';
import { Card, EvaluateExamplePhraseResponse } from '../types/api';
import { useMicrophone, MicState } from '../hooks/useMicrophone';
import { useAuth } from '../hooks/useAuth';
import { getLanguage, getMicLocale } from '../utils/language';
import { speak } from '../utils/speechUtils';

interface PracticePhrasesProps {
  cardId: number;
  cardName: string;
  onClose: () => void;
  onNext?: () => void;
  /** When embedded, only the inner form/feedback is rendered (e.g. inside Practice Card wizard). */
  variant?: 'modal' | 'embedded';
  /** When true, successful phrase AI does not update review; parent handles once per session. */
  suppressReviewLogging?: boolean;
  onSessionFeedbackSuccess?: () => void | Promise<void>;
  /** Overrides the default label above the phrase textarea. */
  inputLabel?: string;
  /** Overrides the textarea placeholder. */
  inputPlaceholder?: string;
  /** When set with {@link embeddedTaskClassName}, wraps instruction + form in a soft container; AI feedback renders below. */
  embeddedInstruction?: React.ReactNode;
  embeddedTaskClassName?: string;
}

function MicButton({ micState, onStart, onStop }: { micState: MicState; onStart: () => void; onStop: () => void }) {
  if (micState === 'transcribing') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Transcribing…
      </span>
    );
  }
  if (micState === 'recording') {
    return (
      <button
        type="button"
        onClick={onStop}
        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
        title="Stop recording"
      >
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
        Stop
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onStart}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded-md transition-colors"
      title="Speak your phrase"
    >
      <Mic className="h-3.5 w-3.5" />
      Speak
    </button>
  );
}

export const PracticePhrases: React.FC<PracticePhrasesProps> = ({
  cardId,
  cardName,
  onClose,
  onNext,
  variant = 'modal',
  suppressReviewLogging = false,
  onSessionFeedbackSuccess,
  inputLabel,
  inputPlaceholder,
  embeddedInstruction,
  embeddedTaskClassName,
}) => {
  const { user } = useAuth();
  const language = getLanguage(user?.email ?? '');
  const micLocale = getMicLocale(user?.email ?? '');
  const [phrase, setPhrase] = useState('');
  const mic = useMicrophone((t) => setPhrase((prev) => prev ? `${prev} ${t}` : t), 'example sentence', micLocale.split('-')[0]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [aiFeedback, setAiFeedback] = useState<EvaluateExamplePhraseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Session history tracking
  const [previousPhrases, setPreviousPhrases] = useState<string[]>([]);
  const [previousRefinedPhrases, setPreviousRefinedPhrases] = useState<string[]>([]);
  
  // Track if we've already updated review count for this cardId
  const hasUpdatedReviewRef = useRef<number | null>(null);
  const [card, setCard] = useState<Card | null>(null);

  // Load card and update review tracking
  useEffect(() => {
    loadCard();
    // Reset the ref when cardId changes
    hasUpdatedReviewRef.current = null;
    // Reset session history when cardId changes
    setPreviousPhrases([]);
    setPreviousRefinedPhrases([]);
  }, [cardId]);

  // Reset session history when component unmounts
  useEffect(() => {
    return () => {
      setPreviousPhrases([]);
      setPreviousRefinedPhrases([]);
    };
  }, []);

  // Note: Review tracking happens after successful AI feedback, not on card load

  const loadCard = async () => {
    try {
      const cardData = await apiClient.getCard(cardId);
      setCard(cardData);
    } catch (err: any) {
      console.error('Error loading card:', err);
      setError('Failed to load card');
    }
  };

  const updateLastReviewed = async () => {
    if (!card) return;

    try {
      // Get current review record
      const currentReview = await apiClient.getCardReview(cardId);
      const currentReviewCount = currentReview?.review_count || 0;
      const newReviewCount = currentReviewCount + 1;
      const now = new Date().toISOString();

      // Log the review
      await apiClient.logCardReview(cardId, {
        last_reviewed_at: now,
        review_count: newReviewCount,
      });

      // Update local state
      setCard({
        ...card,
        last_reviewed_at: now,
        review_count: newReviewCount,
      });
    } catch (err: any) {
      console.error('Error updating last_reviewed_at and review_count:', err);
      // Don't show error to user, just log it
    }
  };

  const handleGetAIFeedback = async () => {
    if (!phrase.trim()) return;

    try {
      setIsLoadingFeedback(true);
      setAiFeedback(null);
      setError(null);

      const response = await apiClient.evaluateExamplePhrase({
        word: cardName,
        example_phrase: phrase.trim(),
        language,
        previous_phrases: previousPhrases,
        previous_refined_phrases: previousRefinedPhrases,
      });

      setAiFeedback(response);
      
      // Update session history for next request
      setPreviousPhrases([...previousPhrases, phrase.trim()]);
      // alternative_phrase is always provided, so always add it to history
      setPreviousRefinedPhrases([...previousRefinedPhrases, response.alternative_phrase]);
      
      if (suppressReviewLogging) {
        await onSessionFeedbackSuccess?.();
      } else if (hasUpdatedReviewRef.current !== cardId) {
        hasUpdatedReviewRef.current = cardId;
        await updateLastReviewed();
      }
    } catch (err: any) {
      console.error('Error getting AI feedback:', err);
      setError(err.response?.data?.detail || 'Failed to get AI feedback. Please try again.');
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setPhrase('');
    setAiFeedback(null);
    setError(null);
    setPreviousPhrases([]);
    setPreviousRefinedPhrases([]);
    onClose();
  };

  const handleNext = () => {
    setPhrase('');
    setAiFeedback(null);
    setError(null);
    setPreviousPhrases([]);
    setPreviousRefinedPhrases([]);
    onNext?.();
  };

  const errorBlock = error && (
    <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
  );

  const fieldBlock = (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">
          {inputLabel ?? `Enter a phrase using "${cardName}"`}
        </label>
        <div className="flex items-center gap-2">
          <MicButton micState={mic.micState} onStart={mic.startRecording} onStop={mic.stopRecording} />
          <button
            type="button"
            disabled={!phrase.trim()}
            onClick={() => speak(phrase.trim(), { lang: micLocale })}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Listen to your phrase"
          >
            <Volume2 className="h-3.5 w-3.5" />
            Listen
          </button>
        </div>
      </div>
      {mic.micError && <p className="text-xs text-red-500 mb-1">{mic.micError}</p>}
      <textarea
        value={phrase}
        onChange={(e) => setPhrase(e.target.value)}
        placeholder={inputPlaceholder ?? 'Write or speak a phrase…'}
        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
        rows={3}
      />
    </div>
  );

  const primaryButton = (
    <button
      type="button"
      onClick={handleGetAIFeedback}
      disabled={isLoadingFeedback || !phrase.trim()}
      className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all"
    >
      {isLoadingFeedback ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Getting AI Feedback...</span>
        </>
      ) : (
        <>
          <Sparkles className="h-5 w-5" />
          <span>Get AI Feedback</span>
        </>
      )}
    </button>
  );

  const phraseFeedbackBlock = aiFeedback && (
    <div className="mt-6 space-y-4 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
      <div className="flex items-center flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 font-medium">Word use:</span>
          <span
            className={`px-3 py-1 rounded text-sm font-semibold ${
              aiFeedback.naturalness === 'correct' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {aiFeedback.naturalness === 'correct' ? 'CORRECT' : 'Needs work'}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 font-medium">Tone:</span>
          <span
            className={`px-3 py-1 rounded text-sm font-medium ${
              aiFeedback.tone === 'formal'
                ? 'bg-gray-100 text-gray-800'
                : aiFeedback.tone === 'neutral'
                  ? 'bg-gray-50 text-gray-700'
                  : 'bg-blue-50 text-blue-700'
            }`}
          >
            {aiFeedback.tone.toUpperCase()}
          </span>
        </div>
      </div>

      {aiFeedback.naturalness === 'incorrect' && aiFeedback.naturalness_reason?.trim() && (
        <div className="text-sm text-gray-700 bg-red-50/80 border border-red-200 rounded p-3">
          <span className="font-medium text-gray-700">Why?</span> {aiFeedback.naturalness_reason}
        </div>
      )}

      {aiFeedback.grammar.has_errors && aiFeedback.grammar.errors && (
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <h4 className="text-sm font-semibold text-red-900 mb-2">Grammar Errors</h4>
          <ul className="list-disc list-inside space-y-1">
            {aiFeedback.grammar.errors.map((err, index) => (
              <li key={index} className="text-sm text-red-800">
                {err}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!aiFeedback.grammar.has_errors && (
        <div className="bg-green-50 border border-green-200 rounded p-4">
          <p className="text-sm text-green-800 font-medium">✓ No grammar errors</p>
        </div>
      )}

      <div className="border border-gray-200 rounded p-4 bg-gray-50/50">
        <h4 className="text-sm font-medium text-gray-600 mb-2">Alternative Phrase</h4>
        <p className="text-sm text-gray-700 break-words">{aiFeedback.alternative_phrase}</p>
      </div>
    </div>
  );

  const phraseContent = (
    <>
      {errorBlock}
      {fieldBlock}
      {primaryButton}
      {phraseFeedbackBlock}
    </>
  );

  if (variant === 'embedded') {
    if (embeddedTaskClassName) {
      return (
        <div className="space-y-6">
          <div className={embeddedTaskClassName}>
            <div className="space-y-5">
              {embeddedInstruction}
              {errorBlock}
              {fieldBlock}
              {primaryButton}
            </div>
          </div>
          {phraseFeedbackBlock}
        </div>
      );
    }
    return <div className="space-y-6">{phraseContent}</div>;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Practice Phrases</h2>
            <p className="text-sm text-gray-500 mt-1">
              Word: <span className="font-medium">{cardName}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">{phraseContent}</div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {onNext && (
            <button
              onClick={handleNext}
              className="px-5 py-2.5 text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors flex items-center space-x-2"
            >
              <ChevronRight className="h-4 w-4" />
              <span>Next</span>
            </button>
          )}
          <button
            onClick={handleClose}
            className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
